import { JSX, ReactNode, createContext, useContext } from 'react';

// Facts about the account — identical for every user of the account. Only
// Country is universal; the capability flags matter to the action layer and
// may be omitted by read-only hosts (the iframe until A3). Flags are real
// booleans: each host converts its own wire shape ('0'/'1' strings or
// booleans, depending on the source) before mounting.
export type AccountingSoftwareAccount = {
  Country: string;
  // The VoPay account id — the Railz connect widget registers under it.
  AccountID?: string;
  AccountName?: string;
  ClientAccountsEnabled?: boolean;
  LoginUserName?: string;
  EFTCollectEnabled?: boolean;
  EFTSendEnabled?: boolean;
  ACHFundEnabled?: boolean;
  ACHWithdrawEnabled?: boolean;
  RTRWithdrawEnabled?: boolean;
  RTPWithdrawEnabled?: boolean;
  InteracMoneyRequestEnabled?: boolean;
  InteracBulkPayoutEnabled?: boolean;
  PushToCardEnabled?: boolean;
  CreditCardEnabled?: boolean;
  PayLinkEnabled?: boolean;
  LockedSelectOptions?: Record<string, string>;
  IQ11StandardEnabled?: boolean;
  IQ11BoostEnabled?: boolean;
  IQ11PlusEnabled?: boolean;
  IQ11AdvantageEnabled?: boolean;
  IQ11DataEnrichingEnabled?: boolean;
  IQ11DataEnrichingEnhancedEnabled?: boolean;
  // Payment-method verification features — gate the contact drawer's verify
  // flows; absent = locked upsell.
  MicroDepositBankAccountVerificationEnabled?: boolean;
  InteracBasicVerificationEnabled?: boolean;
  InteracEnhancedVerificationEnabled?: boolean;
  CardBasicVerificationEnabled?: boolean;
  CardEnhancedVerificationEnabled?: boolean;
};

export type AccountingSoftwareUserPermissions = {
  // Gates the whole action layer: selection, pay/collect buttons and dialogs.
  payAndCollect: boolean;
  // eLinx request options inside the pay/collect dialogs. Also gate the
  // contact drawer's Payment Methods tab "Request" option, alongside
  // CreateElinxConnect below.
  CreateElinxPay?: boolean;
  CreateElinxCollect?: boolean;
  // Also an eLinx request option in the contact drawer's Payment Methods tab
  // only — not used by the pay/collect dialogs above.
  CreateElinxConnect?: boolean;
  // Gates the contact drawer's bank balance/transactions section.
  ViewBankAccountTransactionHistory?: boolean;
  // Gates the drawer's bank-account verify flow (paylink settings lookup).
  ViewElinxSettings?: boolean;
  // Gates the contact drawer's edit/activate/deactivate actions, label
  // management, and the Payment Methods tab's "Provide" option. For a client
  // account the edit/activate/deactivate, label and "Provide" gates use
  // ManageClientAccounts instead.
  ManageContacts?: boolean;
  // Gates a client account's edit/activate/deactivate actions, label
  // management and "Provide" option (the client-account counterpart of
  // ManageContacts).
  ManageClientAccounts?: boolean;
};

export type AccountingSoftwareHostActions = {
  // Insufficient-funds dialog's "add funds" confirmation.
  onFundAccount?: () => void;
  // A locked select option was clicked (the host may show its upsell dialog).
  onLockedFeature?: (featureKey: string) => void;
  // Open the create-invoice/bill page (absent = no create button; the host
  // owns the route and gates it on the user's create permission).
  onCreateDocument?: (documentType: 'invoice' | 'bill') => void;
  // Navigate the HOST app to one of its own pages (link-outs: transaction
  // details, eLinx history, support tickets...). Hosts that navigate in-app
  // (the portal) pass the optional router state through; the embed's message
  // bridge carries only the path. Absent = link-outs render as plain text.
  onNavigate?: (path: string, state?: unknown) => void;
};

type AccountingSoftwareCtx = {
  account: AccountingSoftwareAccount;
  userPermissions: AccountingSoftwareUserPermissions;
  hostActions: AccountingSoftwareHostActions;
};

const AccountingSoftwareContext = createContext<AccountingSoftwareCtx | null>(null);

export function AccountingSoftwareProvider({
  account,
  userPermissions,
  hostActions,
  children,
}: AccountingSoftwareCtx & { children: ReactNode }): JSX.Element {
  return (
    <AccountingSoftwareContext.Provider value={{ account, userPermissions, hostActions }}>
      {children}
    </AccountingSoftwareContext.Provider>
  );
}

// Internal to the feature — exported components take props instead.
export function useAccountingSoftware(): AccountingSoftwareCtx {
  const ctx = useContext(AccountingSoftwareContext);
  if (!ctx) throw new Error('useAccountingSoftware must be used within AccountingSoftware');

  return ctx;
}
